import React, { useEffect, useState } from 'react';

/**
 * CenterRouteLoader
 *
 * Branded full-page loader to show ONLY when a section/page actually takes
 * longer than `delayMs` (default 600ms) to render its content.
 *
 * Use cases:
 *   - Async data screens (BlogList, AdminLeads) that may be slow on first load.
 *   - Optionally as a fallback for code-split chunks.
 *
 * Behavior:
 *   - Mounts hidden; only renders after `delayMs` has elapsed.
 *   - Uses CSS-only animation (route icon pulse + dotted ring) so it doesn't
 *     pull in extra deps for a tiny screen state.
 *   - Respects `prefers-reduced-motion` via the global stylesheet.
 *
 * Accessibility:
 *   - aria-live="polite" so screen readers announce "Routing traffic signals…"
 *     after a short delay rather than on every micro-interaction.
 */
export default function CenterRouteLoader({
  delayMs = 600,
  label = 'Routing traffic signals…',
}) {
  const [show, setShow] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setShow(true), delayMs);
    return () => clearTimeout(t);
  }, [delayMs]);

  if (!show) return null;

  return (
    <div
      role="status"
      aria-live="polite"
      data-testid="center-route-loader"
      className="center-route-loader"
    >
      <div className="center-route-loader__inner">
        <div className="center-route-loader__map" aria-hidden="true">
          {/* Concentric pulse rings */}
          <span className="ring ring-1" />
          <span className="ring ring-2" />
          <span className="ring ring-3" />
          {/* Central route mark */}
          <svg
            width="36"
            height="36"
            viewBox="0 0 36 36"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            className="center-route-loader__mark"
          >
            <path
              d="M9 23 L18 9 L27 23"
              stroke="url(#grad-route)"
              strokeWidth="2.4"
              strokeLinecap="round"
              strokeLinejoin="round"
              fill="none"
            />
            <circle cx="18" cy="22" r="2.4" fill="#FBBF24" />
            <defs>
              <linearGradient id="grad-route" x1="0" y1="0" x2="36" y2="36" gradientUnits="userSpaceOnUse">
                <stop stopColor="#22D3EE" />
                <stop offset="1" stopColor="#8B5CF6" />
              </linearGradient>
            </defs>
          </svg>
        </div>
        <p className="center-route-loader__text">{label}</p>
      </div>
    </div>
  );
}
